'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import Image from 'next/image';
import Link from 'next/link';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useInView } from 'react-intersection-observer';
import VideoComponent from '@/components/hero/VideoComponent';

interface Item {
  heading: string;
  video_products: {
    video: { id: string; mux_playback_id?: string | null };
    thumbnail: { id: string };
    product: {
      title: string;
      handle: string;
      thumbnail_preview_url: string;
      variants: {
        rrp: string;
      }[];
    };
  }[];
}

const VideoSwipeModule = ({ item }: { item: Item }) => {
  const swiperRef = useRef<any>(null);
  const { ref, inView } = useInView({
    triggerOnce: false,
    rootMargin: '-30% 0% -30% 0%',
  });

  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const prevSelectedIndexRef = useRef<number>(0);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [restartStates, setRestartStates] = useState<Map<number, number>>(
    new Map()
  );

  const getProducts = useCallback(() => {
    if (!item?.video_products) return [];
    return [
      ...item.video_products,
      ...item.video_products,
      ...item.video_products,
    ];
  }, [item?.video_products]);

  const products = getProducts();

  const handleNextButtonClick = () => {
    swiperRef.current.slideNext();
  };

  const handlePrevButtonClick = () => {
    swiperRef.current.slidePrev();
  };

  const handleSlideClick = (index: number) => {
    if (!swiperRef.current || isTransitioning || index === selectedIndex)
      return;

    setIsTransitioning(true);

    // Use slideToLoop with the clicked index directly
    swiperRef.current.slideToLoop(index, 500);

    // Reset transition state
    setTimeout(() => {
      setIsTransitioning(false);
    }, 1000);
  };

  const handleSlideChange = useCallback((swiper: any) => {
    const newIndex = swiper.realIndex;
    const prevIndex = prevSelectedIndexRef.current;
    setSelectedIndex(newIndex);
    setIsTransitioning(true);

    if (prevIndex !== newIndex) {
      setRestartStates((prev) => {
        const next = new Map(prev);
        const currentCount = next.get(prevIndex) ?? 0;
        next.set(prevIndex, currentCount + 1);
        return next;
      });
      prevSelectedIndexRef.current = newIndex;
    }

    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
    }, 50);
  }, []);

  // Pause video when section is out of viewport
  useEffect(() => {
    if (!inView) {
      setRestartStates((prev) => {
        const next = new Map(prev);
        products.forEach((_, idx) => {
          const currentCount = next.get(idx) ?? 0;
          next.set(idx, currentCount + 1);
        });
        return next;
      });
    }
  }, [inView, products.length]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full flex flex-col py-14 gap-6 items-center justify-center">
      <h2 className="font-bold text-center text-2xl leading-[32px] md:text-[32px] md:leading-[48px] mb-2 px-4">
        {item?.heading}
      </h2>

      <div ref={ref} className="w-full">
        <Swiper
          ref={swiperRef}
          slidesPerView={5}
          spaceBetween={15}
          centeredSlides={true}
          onSlideChange={handleSlideChange}
          loop={true}
          loopAddBlankSlides
          loopAdditionalSlides={2}
          speed={500}
          breakpoints={{
            300: {
              slidesPerView: 1.5,
            },
            800: {
              slidesPerView: 3.5,
            },
            1064: {
              slidesPerView: 5,
            },
            1800: {
              slidesPerView: 5,
            },
          }}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
        >
          {products?.map((value, idx: number) => {
            const isSelected = idx === selectedIndex;

            return (
              <SwiperSlide key={idx}>
                <div
                  className={`flex flex-col ${isSelected ? 'opacity-100' : 'opacity-50'} transition-all duration-200 gap-3 md:h-[660px] h-[550px] items-center justify-center`}
                >
                  <div
                    onClick={() => handleSlideClick(idx)}
                    style={{ backgroundColor: '#f0f0f0' }} // Prevent white flash
                    className={`rounded-[10px] w-full cursor-pointer overflow-hidden relative md:transition-all md:duration-300 ${idx === selectedIndex ? 'md:h-[560px] h-[450px]' : 'h-[450px]'}`}
                  >
                    <VideoComponent
                      file={value?.video?.id}
                      thumbnail={value?.thumbnail?.id}
                      autoplay={isSelected && inView}
                      preload={isSelected ? 'auto' : 'metadata'}
                      loop={true}
                      sound={isSelected}
                      restart={restartStates.get(idx) ?? 0}
                      mux_playback_id={value?.video?.mux_playback_id}
                      eager={isSelected}
                    />
                  </div>

                  <Link
                    href={`/product/${value?.product?.handle}`}
                    className="w-full group h-20 rounded-[5px] border-2 border-gray p-1 flex items-center gap-2"
                  >
                    <div className="w-16 shrink-0 h-full flex items-center justify-center relative rounded-sm overflow-hidden">
                      <Image
                        alt="img"
                        src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${value?.product?.thumbnail_preview_url}`}
                        fill
                        sizes="64px"
                        className="object-contain"
                      />
                    </div>

                    <div className="flex flex-col gap-1 items-start text-[12px] font-semibold">
                      <p className="group-hover:underline line-clamp-2">
                        {value?.product?.title}
                      </p>
                      {value?.product?.variants[0]?.rrp && (
                        <p className="product-price">
                          ${Number(value?.product?.variants[0]?.rrp).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </Link>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
      <div className="flex z-10 space-x-2 mb-1 w-full overflow-x-auto no-scroll px-4">
        {item?.video_products.map((value, index) => {
          return (
            <div
              key={index}
              className={cn(
                'py-2 cursor-pointer font-semibold text-sm md:text-base whitespace-nowrap relative z-10 first:!ml-auto last:!mr-auto '
              )}
              onClick={() => {
                handleSlideClick(index);
              }}
            >
              <div
                className={`w-3 h-3 rounded-full ${selectedIndex % item?.video_products?.length === index ? 'opacity-100' : 'opacity-20'} bg-black`}
              ></div>
            </div>
          );
        })}
      </div>
      <div className="w-full flex items-center justify-center gap-3">
        <div
          onClick={handlePrevButtonClick}
          className="w-[40px] h-[40px] cursor-pointer transition-all duration-100 rounded-full group hover:bg-black flex items-center justify-center border-2 border-black"
        >
          <CaretLeft
            size={24}
            weight="bold"
            className="group-hover:invert transition-all duration-100"
          />
        </div>

        <div
          onClick={handleNextButtonClick}
          className="w-[40px] h-[40px] cursor-pointer transition-all duration-100 rounded-full group hover:bg-black flex items-center justify-center border-2 border-black"
        >
          <CaretRight
            size={24}
            weight="bold"
            className="group-hover:invert transition-all duration-100"
          />
        </div>
      </div>
    </div>
  );
};

export default VideoSwipeModule;
